/**
 * `toMarkdown(doc, labels)` (architecture §11.2): P1 export, P2 API and webhooks, GitHub linkbacks. Built on
 * `prosemirror-markdown`'s serializer with our node names; mentions resolve through the mandatory `LabelResolver`
 * (placeholders for anything the output's principal can't see); links are re-validated.
 */
import { MarkdownSerializer, type MarkdownSerializerState } from 'prosemirror-markdown';
import type { Mark, Node as PMNode } from '@tiptap/pm/model';
import type { DocJSON, Reference } from './doc';
import { safeHref } from './links';
import { editorSchema } from './schema/index';
import { resolveMention, type LabelResolver } from './text';

function serializer(labels: LabelResolver, publicUrl: string): MarkdownSerializer {
  const list = (state: MarkdownSerializerState, node: PMNode, marker: (i: number) => string): void => {
    state.renderList(node, '  ', marker);
  };
  return new MarkdownSerializer(
    {
      doc: (state, node) => state.renderContent(node),
      paragraph: (state, node) => {
        state.renderInline(node);
        state.closeBlock(node);
      },
      heading: (state, node) => {
        state.write(`${'#'.repeat(Number(node.attrs.level))} `);
        state.renderInline(node);
        state.closeBlock(node);
      },
      blockquote: (state, node) => state.wrapBlock('> ', null, node, () => state.renderContent(node)),
      codeBlock: (state, node) => {
        const lang = typeof node.attrs.language === 'string' && node.attrs.language !== 'plaintext' ? node.attrs.language : '';
        const fence = node.textContent.includes('```') ? '~~~~' : '```';
        state.write(`${fence}${lang}\n`);
        state.text(node.textContent, false);
        state.ensureNewLine();
        state.write(fence);
        state.closeBlock(node);
      },
      horizontalRule: (state, node) => {
        state.write('---');
        state.closeBlock(node);
      },
      bulletList: (state, node) => list(state, node, () => '- '),
      orderedList: (state, node) => {
        const start = Number(node.attrs.start ?? 1);
        list(state, node, (i) => `${start + i}. `);
      },
      taskList: (state, node) => list(state, node, () => '- '),
      listItem: (state, node) => state.renderContent(node),
      taskItem: (state, node) => {
        state.write(node.attrs.checked === true ? '[x] ' : '[ ] ');
        state.renderContent(node);
      },
      hardBreak: (state, node, parent, index) => {
        for (let i = index + 1; i < parent.childCount; i++) {
          if (parent.child(i).type !== node.type) {
            state.write('\\\n');
            return;
          }
        }
      },
      text: (state, node) => state.text(node.text ?? ''),
      mention: (state, node) => {
        const kind = node.attrs.kind as Reference['kind'];
        const id = String(node.attrs.id);
        const { label, visible } = resolveMention(kind, id, labels);
        state.text(label);
        const url = visible && kind !== 'user' ? labels.url?.(kind, id) : undefined;
        if (url) state.write(` (${url})`);
      },
      image: (state, node) => {
        const url = labels.url?.('file', String(node.attrs.fileId));
        state.write(url ? `![image](${url})` : '[image]');
        state.closeBlock(node);
      },
    },
    {
      bold: { open: '**', close: '**', mixable: true, expelEnclosingWhitespace: true },
      italic: { open: '*', close: '*', mixable: true, expelEnclosingWhitespace: true },
      strike: { open: '~~', close: '~~', mixable: true, expelEnclosingWhitespace: true },
      code: { open: '`', close: '`', escape: false },
      link: {
        open: (_state: MarkdownSerializerState, mark: Mark) => (safeHref(mark.attrs.href, publicUrl) ? '[' : ''),
        close: (_state: MarkdownSerializerState, mark: Mark) => {
          const href = safeHref(mark.attrs.href, publicUrl);
          return href ? `](${href.replace(/[()]/g, '\\$&')})` : '';
        },
      },
    },
  );
}

/** Markdown of a stored document for the principal `labels` is bound to. `publicUrl` validates same-origin links. */
export function toMarkdown(doc: DocJSON, labels: LabelResolver, publicUrl = 'http://localhost'): string {
  return serializer(labels, publicUrl).serialize(editorSchema().nodeFromJSON(doc));
}
